import { Box, Stack, Button } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate, Outlet } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useState } from "react";
import { z } from "zod";

import type { GetCatalogAssetsParams } from "@/types/catalogAssets";
import type { QueryClient } from "@tanstack/react-query";

import { type AssetMessage } from "@/api/connectAssets";
import { catalogAssetsQueryOptions } from "@/api/products";
import { TilesPagination } from "@/components/TilesPagination/TilesPagination";
import { AssetEmptyState } from "@/features/AssetLibrary/components/AssetEmptyState";
import { AssetFilterRow } from "@/features/AssetLibrary/components/AssetFilterRow";
import { AssetGrid } from "@/features/AssetLibrary/components/AssetGrid";
import { AssetTable } from "@/features/AssetLibrary/components/AssetTable";
import { DeleteAssetDialog } from "@/features/AssetLibrary/components/DeleteAssetDialog";
import { catalogAssetToAssetMessage } from "@/features/AssetLibrary/utils/catalogAssetAdapter";
import { UploadAssetsModal } from "@/features/AssetUpload/components/UploadAssetsModal";
import { OrionPageHeader } from "@/orion/OrionComponents/OrionPageHeader";

const PAGE_SIZE = 50;

export type AssetLibrarySearch = {
  search?: string;
  view?: "grid" | "list";
  page?: number;
};

const SearchSchema = z.object({
  search: z.string().optional(),
  view: z.enum(["grid", "list"]).optional(),
  page: z
    .union([z.string(), z.number()])
    .transform((v) => (typeof v === "string" ? Number(v) : v))
    .pipe(z.number().int().positive())
    .catch(1)
    .optional(),
});

function listParamsFromSearch(
  search: AssetLibrarySearch,
): GetCatalogAssetsParams {
  const page = search.page ?? 1;
  return {
    approval_status: ["approved", "draft"],
    limit: PAGE_SIZE,
    next: (page - 1) * PAGE_SIZE,
  };
}

export const Route = createFileRoute("/_auth/assets/$siteId")({
  validateSearch: zodValidator(SearchSchema),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ params, context, deps }) => {
    const { queryClient } = context as { queryClient: QueryClient };
    await queryClient.ensureQueryData(
      catalogAssetsQueryOptions(
        params.siteId,
        listParamsFromSearch(deps.search),
      ),
    );
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { siteId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/assets/$siteId" });
  const view: "grid" | "list" = search.view ?? "grid";
  const page = search.page ?? 1;

  const listParams = listParamsFromSearch(search);
  const listOptions = catalogAssetsQueryOptions(siteId, listParams);
  const { data } = useSuspenseQuery({
    queryKey: listOptions.queryKey,
    queryFn: listOptions.queryFn,
  });

  const searchTerm = (search.search ?? "").trim().toLowerCase();
  const filteredAssets = searchTerm
    ? data.assets.filter((a) =>
        (a.file_name ?? a.title ?? a.handle).toLowerCase().includes(searchTerm),
      )
    : data.assets;
  const viewAssets = filteredAssets.map(catalogAssetToAssetMessage);

  const [pendingDelete, setPendingDelete] = useState<AssetMessage | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadInitialFiles, setUploadInitialFiles] = useState<
    File[] | undefined
  >(undefined);

  const openUploadFromButton = () => {
    setUploadInitialFiles(undefined);
    setUploadOpen(true);
  };

  const openUploadFromDrop = (files: File[]) => {
    setUploadInitialFiles(files);
    setUploadOpen(true);
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setUploadInitialFiles(undefined);
  };

  const hasNextPage = data.next !== null;
  const totalPages = searchTerm ? 1 : hasNextPage ? page + 1 : page;

  const updateSearch = <K extends keyof AssetLibrarySearch>(
    key: K,
    value: AssetLibrarySearch[K] | undefined,
    options?: { resetPage?: boolean },
  ) => {
    void navigate({
      search: (prev): AssetLibrarySearch => {
        const next: AssetLibrarySearch = { ...prev };
        if (value === undefined || value === "") {
          delete next[key];
        } else {
          next[key] = value;
        }
        if (options?.resetPage) {
          next.page = 1;
        }
        return next;
      },
      replace: true,
    });
  };

  const handleAssetClick = (asset: AssetMessage) => {
    void navigate({
      to: "/assets/$siteId/$assetKey",
      params: { siteId, assetKey: asset.asset_key },
      search: (prev) => prev,
    });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <OrionPageHeader
        title="Asset library"
        description="JPEG, PNG, WebP, SVG, MP4, MOV, WebM, AVI — images and videos up to 250MB"
        actions={
          <Button variant="contained" onClick={openUploadFromButton}>
            Upload
          </Button>
        }
      />
      <Stack spacing={4} sx={{ p: 4, flex: 1 }}>
        <AssetFilterRow
          search={search.search ?? ""}
          onSearchChange={(v) =>
            updateSearch("search", v || undefined, { resetPage: true })
          }
          view={view}
          onViewChange={(v) => updateSearch("view", v)}
        />

        {viewAssets.length === 0 ? (
          <AssetEmptyState
            hasSearch={!!search.search}
            searchTerm={search.search}
            onClearSearch={() =>
              updateSearch("search", undefined, { resetPage: true })
            }
            onUpload={openUploadFromButton}
            onFilesDropped={openUploadFromDrop}
          />
        ) : view === "grid" ? (
          <AssetGrid
            assets={viewAssets}
            onAssetClick={handleAssetClick}
            onAssetDelete={setPendingDelete}
          />
        ) : (
          <AssetTable
            assets={viewAssets}
            onAssetClick={handleAssetClick}
            onAssetDelete={setPendingDelete}
          />
        )}

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <TilesPagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => updateSearch("page", newPage)}
            />
          </Box>
        )}
      </Stack>

      <DeleteAssetDialog
        siteId={siteId}
        asset={pendingDelete}
        onClose={() => setPendingDelete(null)}
      />

      <UploadAssetsModal
        open={uploadOpen}
        siteId={siteId}
        initialFiles={uploadInitialFiles}
        initialStep={uploadInitialFiles ? "metadata" : "dropzone"}
        onClose={closeUpload}
      />

      <Outlet />
    </Box>
  );
}
